import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { PreloadingStrategy, Route, RouteConfigLoadEnd, Router } from '@angular/router';

import { TimerService } from '@frontend/vanilla/core';
import { EMPTY, Observable, filter, tap } from 'rxjs';

import { PrefetchRegistry } from './prefetch-registry.service';

@Injectable({ providedIn: 'root' })
export class SpeculativeLinkPreloader implements PreloadingStrategy {
    #loading = new Set<Route>();
    #registry = inject(PrefetchRegistry);
    #timerService = inject(TimerService);
    #injector = inject(Injector);

    constructor() {
        const router = inject(Router);

        router.events
            .pipe(
                filter((event): event is RouteConfigLoadEnd => event instanceof RouteConfigLoadEnd),
                tap((event) => {
                    /**
                     * The idleCallback is necessary because routes have loaded but not yet processed, the fields we
                     * access to load the preResolvers and the required data like _loadRoutes are only present after
                     * it's done processing.
                     */
                    this.#timerService.scheduleIdleCallback(() => {
                        this.#registry.registerRoutes(event.route);
                    });
                }),
            )
            .subscribe();
    }

    preload(route: Route, load: () => Observable<void>): any {
        // Don't preload the same route twice
        if (this.#loading.has(route)) {
            return EMPTY;
        }
        // Don't preload if the user is on 2G. or if Save-Data is enabled.
        const conn = typeof navigator !== 'undefined' ? (navigator as any).connection : undefined;
        if (conn && ((conn.effectiveType || '').includes('2g') || conn.saveData)) {
            return EMPTY;
        }
        // Prevent from preloading based on configured
        if (route.data && route.data['preload'] === false) {
            return EMPTY;
        }

        // Prevent preloading based on guard
        if (!this.#canPreloadGuardResult(route)) {
            return EMPTY;
        }

        if (this.#registry.shouldPrefetch(route)) {
            this.#loading.add(route);
            return load();
        }

        return EMPTY;
    }

    #canPreloadGuardResult(route: Route): boolean {
        if (!route.data) {
            return true;
        }

        const canPreload = route.data['canPreload'];
        if (canPreload === 'function') {
            return runInInjectionContext(this.#injector, () => canPreload(route));
        }

        if (Array.isArray(canPreload)) {
            for (let i = 0; i < canPreload.length; i++) {
                if (!runInInjectionContext(this.#injector, () => canPreload[i](route))) {
                    return false;
                }
            }
            return true;
        }
        return true;
    }
}
